import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { faChartNetwork } from '@fortawesome/pro-solid-svg-icons';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { catchError, of, switchMap, take } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { isRole } from '../../../../domains/app/auth/auth.maps';
import { authenticatedRole } from '../../../../domains/app/auth/auth.selectors';
import {
  CompanyInsightsService,
  FLAG_SEVERITY_COLORS,
  FLAG_SEVERITY_LABELS,
  flagSummary,
  InstallerFlag,
  topSeverity,
} from '../../../../domains/company-insights/company-insights.service';
import { Installer, InstallerTeamLead } from '../../../../domains/users';
import { Connection } from '../../../../domains/users/connection/connection.model';
import { isInstallerLead } from '../../../../domains/users/user/user.model';
import { QrCodeModalComponent } from '../qr-code-modal/qr-code-modal.component';

@Component({
  selector: 'gc-sidebar-header',
  templateUrl: './sidebar-header.component.html',
  styleUrls: ['./sidebar-header.component.scss'],
})
export class SidebarHeaderComponent implements OnChanges {
  @Input() installer!: InstallerTeamLead;
  @Input() connection?: Connection;
  @Input() canEditProfile = false;

  @Output() called = new EventEmitter<Installer>();
  @Output() emailed = new EventEmitter<Installer>();
  @Output() websiteVisited = new EventEmitter<Installer>();
  @Output() optionsOpened = new EventEmitter<{ event: Event; installer: Installer }>();
  @Output() closeProfileModal = new EventEmitter<{ event: Event; installer: Installer }>();
  @Output() editMyProfile = new EventEmitter<{ event: Event; installer: Installer }>();

  protected readonly faChartNetwork = faChartNetwork;

  /** TT-259 on the full profile too (Paul, Sprint Review 10/14): the viewing company's own flags. */
  flags: InstallerFlag[] = [];
  private flagsFor?: string;

  constructor(
    public readonly popoverGC: PopoverController,
    private readonly modalGC: ModalController,
    private readonly store: Store,
    private readonly insights: CompanyInsightsService,
    private readonly alerts: AlertController,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['installer']) {
      this.loadFlags();
    }
  }

  get flagColor(): string | undefined {
    const top = topSeverity(this.flags);
    return top ? FLAG_SEVERITY_COLORS[top] : undefined;
  }

  get flagText(): string {
    return flagSummary(this.flags);
  }

  async showFlags(): Promise<void> {
    const alert = await this.alerts.create({
      header: 'Your company flagged this installer',
      message: this.flags
        .map(flag => `${FLAG_SEVERITY_LABELS[flag.severity]}: ${flag.issue}`)
        .join('<br/>'),
      buttons: ['Close'],
    });
    await alert.present();
  }

  async openQRCodeModal() {
    const profileUrl = `${environment.apiRoot.profiles}${this.installer.externalId}`;
    const modal = await this.modalGC.create({
      component: QrCodeModalComponent,
      componentProps: {
        profileUrl,
      },
    });
    await modal.present();
  }

  /** Only a company sees flags, and only its own; installers never ask. */
  private loadFlags(): void {
    const id = isInstallerLead(this.installer) ? this.installer.id : undefined;
    if (!id || id === this.flagsFor) return;
    this.flagsFor = id;
    this.flags = [];
    this.store
      .select(authenticatedRole)
      .pipe(
        take(1),
        switchMap(role =>
          isRole(role, 'companyAdmin', 'companyManager') ? this.insights.flags(id) : of([] as InstallerFlag[]),
        ),
        catchError(() => of([] as InstallerFlag[])),
      )
      .subscribe(flags => (this.flags = flags));
  }
}
